
import { defineComponent } from "vue";
import store from "@/store";
import router from "@/router";
import SchoolLegalEntityService from "@/api/Configuration/SchoolLegalEntityService";

type AttachmentKey = "logo" | "signature";

export default defineComponent({
    name: "SchoolLegalEntityForm",

    data() {
        return {
            form: {
                record: {} as any,
                saving: false,
            },

            privateEntityOptions: [
                { text: "Sí", value: true },
                { text: "No", value: false },
            ],

            // ✅ one variable per attachment
            logoAttachment: {
                apiFieldName: "logoFile",
                file: null as File | null,
                previewUrl: "",
                objectUrl: undefined as string | undefined,
            },
            signatureAttachment: {
                apiFieldName: "directorSignatureFile",
                file: null as File | null,
                previewUrl: "",
                objectUrl: undefined as string | undefined,
            },
        };
    },

    computed: {
        // ✅ Proxy: guarantees boolean in the UI and prevents "both selected"
        privateEntityProxy: {
            get(): boolean {
                return this.toBoolean(this.form.record?.PrivateEntity);
            },
            set(val: boolean) {
                if (!this.form.record) this.form.record = {};
                this.form.record.PrivateEntity = !!val;
            },
        },
    },

    async mounted() {
        await this.load();
    },

    beforeUnmount() {
        this.cleanupObjectUrl("logo");
        this.cleanupObjectUrl("signature");
    },

    methods: {
        // ✅ normalize different DB/API shapes to boolean
        toBoolean(v: any): boolean {
            if (v === true) return true;
            if (v === false) return false;

            // handle numbers coming from DB
            if (v === 1 || v === "1") return true;
            if (v === 0 || v === "0") return false;

            // handle strings
            if (typeof v === "string") {
                const s = v.trim().toLowerCase();
                if (s === "true" || s === "yes" || s === "si" || s === "sí") return true;
                if (s === "false" || s === "no") return false;
            }

            // null/undefined/empty -> false (your requirement)
            return false;
        },

        async load() {
            try {
                const record = await SchoolLegalEntityService.GetEntity();
                this.form.record = record || ({} as any);

                // ✅ ensure this field is never null in your model
                this.form.record.PublicEntity = this.toBoolean(this.form.record.PublicEntity);

                // previews from stored values (assuming URL)
                this.logoAttachment.previewUrl = this.form.record.LogoFile || "";
                this.signatureAttachment.previewUrl = this.form.record.DirectorSignatureFile || "";
            } catch (err) {
                store.dispatch("core/popupDialog", {
                    text: `Error al cargar institución: ${err}`,
                    type: "error",
                });
            }
        },

        reload() {
            this.load();
        },

        openFileDialog(key: AttachmentKey) {
            const refName = key === "logo" ? "logoInput" : "signatureInput";
            const input = this.$refs[refName] as HTMLInputElement | undefined;
            input?.click();
        },

        cleanupObjectUrl(key: AttachmentKey) {
            const att = key === "logo" ? this.logoAttachment : this.signatureAttachment;
            if (att.objectUrl) {
                URL.revokeObjectURL(att.objectUrl);
                att.objectUrl = undefined;
            }
        },

        onAttachmentChange(e: Event, key: AttachmentKey) {
            const input = e.target as HTMLInputElement;
            const f = input.files?.[0] ?? null;

            const att = key === "logo" ? this.logoAttachment : this.signatureAttachment;

            this.cleanupObjectUrl(key);
            att.file = f;

            if (f) {
                att.objectUrl = URL.createObjectURL(f);
                att.previewUrl = att.objectUrl;
            } else {
                att.previewUrl =
                    key === "logo"
                        ? (this.form.record.LogoFile || "")
                        : (this.form.record.DirectorSignatureFile || "");
            }
        },

        async handleSubmit() {
            try {
                this.form.saving = true;

                // ✅ guarantee boolean before save
                this.form.record.PublicEntity = this.toBoolean(this.form.record.PublicEntity);

                const formData = new FormData();

                formData.append(
                    "entity",
                    new Blob([JSON.stringify(this.form.record)], { type: "application/json" })
                );

                if (this.logoAttachment.file) {
                    formData.append(this.logoAttachment.apiFieldName, this.logoAttachment.file);
                }
                if (this.signatureAttachment.file) {
                    formData.append(this.signatureAttachment.apiFieldName, this.signatureAttachment.file);
                }

                const res = await SchoolLegalEntityService.Post(formData);
                this.form.record = res || this.form.record;

                // ✅ ensure bool after save too
                this.form.record.PublicEntity = this.toBoolean(this.form.record.PublicEntity);

                // refresh previews
                this.logoAttachment.previewUrl = this.form.record.LogoFile || this.logoAttachment.previewUrl;
                this.signatureAttachment.previewUrl =
                    this.form.record.DirectorSignatureFile || this.signatureAttachment.previewUrl;

                store.dispatch("core/popupDialog", {
                    text: "Información de la institución guardada exitosamente.",
                    type: "success",
                });

                router.push(`/configuration/legal-entity`);
                location.reload();
            } catch (err: any) {
                store.dispatch("core/popupDialog", {
                    text: `Error al guardar: ${err?.message || err}`,
                    type: "error",
                });
            } finally {
                this.form.saving = false;
            }
        },
    },
});
